@if (isOpen()) {
  <div
    @openClose
    cdkDrag
    cdkDragBoundary="body"
    [cdkDragFreeDragPosition]="dragPosition()"
    [cdkDragConstrainPosition]="constrainDragPosition"
    (cdkDragEnded)="onDragEnded($event)"
    class="assistant-gradient-border relative flex flex-col overflow-hidden rounded-[20px] shadow-modal"
    [style.width.px]="width()"
    [style.height.px]="height()"
    data-testid="assistant-chat-window-root"
  >
    <div cdkDragHandle class="flex cursor-move items-center justify-between gap-2 px-5 py-3" data-testid="assistant-chat-window-header">
      <div class="flex items-center gap-2 overflow-hidden">
        <svg-icon key="px-48-assistant" size="lg" class="shrink-0"></svg-icon>
        <span class="typo-h5-medium whitespace-nowrap text-states-purple-dark">{{ 'assistant_chat_window_title' | translate }}</span>
        @if (isInsideTrialPeriod() && siteId()) {
          <lib-assistant-trial-badge [siteId]="siteId()!"></lib-assistant-trial-badge>
        }
      </div>
      <button type="button" class="text-text-secondary" data-testid="assistant-chat-window-close" (click)="onCloseClick()">
        <svg-icon key="px-16-x" size="sm"></svg-icon>
      </button>
    </div>

    @if (isLoading()) {
      <div class="flex flex-1 items-center justify-center" data-testid="assistant-chat-window-loading">
        <ng-lottie currentcolor class="h-8 w-8 text-primary-blue" [options]="lottieOptions"></ng-lottie>
      </div>
    } @else {
      <div class="relative flex flex-1 flex-col overflow-hidden">
        @if (replyingToMessage(); as thread) {
          <lib-reply-overlay
            [message]="thread"
            [threadMessages]="assistantMessagesService.threadMessages()"
            [participantCount]="assistantMessagesService.participantCount()"
            [noMoreThreadMessages]="assistantMessagesService.noMoreThreadMessages()"
            [editingMessageId]="editingMessageId()"
            (closeOverlay)="onCloseReply()"
            (loadOlderThreadMessages)="loadOlderThreadMessages()"
            (unsendMessage)="onUnsendMessage($event)"
            (startEdit)="onStartEdit($event)"
            (cancelEdit)="onCancelMessageEdit()"
            (editMessage)="onEditMessage($event)"
          ></lib-reply-overlay>
        }
        <lib-assistant-message-list
          [messages]="messages()"
          [showWelcome]="showWelcome()"
          [welcomeTitle]="welcomeBanner()?.title ?? null"
          [welcomeDescription]="welcomeBanner()?.description ?? null"
          [participantCount]="assistantMessagesService.participantCount()"
          [newMessageMarkerBeforeMessageId]="assistantMessagesService.newMessageMarkerBeforeMessageId()"
          [editingMessageId]="editingMessageId()"
          [historyOpenMessageId]="historyOpenMessageId()"
          [quickActionContext]="quickActionContext()"
          (namedQuickAction)="onNamedQuickAction($event)"
          (loadOlderMessages)="assistantMessagesService.loadOlderMessages()"
          (unsendMessage)="onUnsendMessage($event)"
          (retrySendMessage)="onRetrySendMessage($event)"
          (markMessageUnread)="onMarkMessageUnread($event)"
          (reply)="onReply($event)"
          (threadClicked)="onThreadClick($event)"
          (startEdit)="onStartEdit($event)"
          (cancelEdit)="onCancelMessageEdit()"
          (toggleHistory)="onToggleHistory($event)"
          (editMessage)="onEditMessage($event)"
        ></lib-assistant-message-list>
      </div>
    }
    <div class="assistant-chat-window-text-background flex min-h-0 flex-col gap-2 pt-4">
      <!-- The Support Center and Learning Lab pills are seeded global quick actions now (AC-17); the row
           is hidden while the collab loads, whenever nothing qualifies, and for FFD Notes (no support center or
           learning lab to link to). The 100px start padding lines the pills up with the text box: the input's
           20px padding, plus its 32px attach and 32px record buttons and their two 8px grid gaps. -->
      @if (showLinkPills()) {
        <lib-collab-quick-action-row
          class="pe-5 ps-[100px]"
          data-testid="assistant-chat-window-quick-actions"
          [quickActions]="quickActions()"
          [context]="quickActionContext()"
          (namedQuickAction)="onNamedQuickAction($event)"
        ></lib-collab-quick-action-row>
      }
      <!-- Keyed by the assistant collaboration: a site or user switch remounts the input with that collaboration's draft. -->
      @for (draftCollabId of [assistantMessagesService.collabId()]; track draftCollabId) {
        <lib-message-input
          libAssistantMessageInputScope
          class="assistant-chat-window-message-input min-h-0 px-5 pb-4"
          [hideAttachmentActions]="true"
          [showPhotoAttachments]="true"
          [showScreenRecording]="true"
          [preserveDraft]="true"
          [hideContextMenu]="true"
          [showPlaceholderIcon]="false"
          [inThread]="!!replyingToMessage()"
          (send)="onSend($event)"
          (recordClick)="onRecordClick()"
        >
        </lib-message-input>
      }
    </div>

    <!-- Resize handles: edges -->
    <div
      class="absolute left-2 right-2 top-0 z-10 h-2 cursor-ns-resize"
      data-testid="assistant-chat-window-resize-n"
      (mousedown)="onResizeStart($event, 'n')"
    ></div>
    <div
      class="absolute bottom-0 left-2 right-2 z-10 h-2 cursor-ns-resize"
      data-testid="assistant-chat-window-resize-s"
      (mousedown)="onResizeStart($event, 's')"
    ></div>
    <div
      class="absolute bottom-2 right-0 top-2 z-10 w-2 cursor-ew-resize"
      data-testid="assistant-chat-window-resize-e"
      (mousedown)="onResizeStart($event, 'e')"
    ></div>
    <div
      class="absolute bottom-2 left-0 top-2 z-10 w-2 cursor-ew-resize"
      data-testid="assistant-chat-window-resize-w"
      (mousedown)="onResizeStart($event, 'w')"
    ></div>
    <!-- Resize handles: corners -->
    <div
      class="absolute right-0 top-0 z-10 size-4 cursor-nesw-resize"
      data-testid="assistant-chat-window-resize-ne"
      (mousedown)="onResizeStart($event, 'ne')"
    ></div>
    <div
      class="absolute left-0 top-0 z-10 size-4 cursor-nwse-resize"
      data-testid="assistant-chat-window-resize-nw"
      (mousedown)="onResizeStart($event, 'nw')"
    ></div>
    <div
      class="absolute bottom-0 right-0 z-10 size-4 cursor-nwse-resize"
      data-testid="assistant-chat-window-resize-se"
      (mousedown)="onResizeStart($event, 'se')"
    ></div>
    <div
      class="absolute bottom-0 left-0 z-10 size-4 cursor-nesw-resize"
      data-testid="assistant-chat-window-resize-sw"
      (mousedown)="onResizeStart($event, 'sw')"
    ></div>
  </div>
}
